<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FilterBar from '@lib/components/shared/FilterBar.vue'
import BInput from '@lib/components/base/BInput.vue'
import BSelect from '@lib/components/base/BSelect.vue'
import type { UserFilters } from '@/composables/useUsersData'

const props = defineProps<{ modelValue: UserFilters }>()
const emit = defineEmits<{
    (e: 'update:modelValue', v: UserFilters): void
    (e: 'clear'): void
}>()

const { t } = useI18n()

const activeOptions = computed(() => [
    { value: 'all', label: t('users.filter.activeOptions.all') },
    { value: 'true', label: t('users.filter.activeOptions.active') },
    { value: 'false', label: t('users.filter.activeOptions.inactive') }
])

const activeCount = computed(() => {
    let n = 0
    if (props.modelValue.search) n++
    if (props.modelValue.active !== 'all') n++
    return n
})

function update<K extends keyof UserFilters>(key: K, value: UserFilters[K]) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>

<template>
    <FilterBar :active-count="activeCount" @clear="$emit('clear')">
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('users.filter.search') }}</span>
            <BInput
                :model-value="modelValue.search"
                :placeholder="t('users.filter.search')"
                @update:model-value="v => update('search', v)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('users.filter.active') }}</span>
            <BSelect
                :model-value="modelValue.active"
                :options="activeOptions"
                option-label="label"
                option-value="value"
                :placeholder="t('users.filter.active')"
                :show-clear="false"
                @update:model-value="v => update('active', v as UserFilters['active'])"
            />
        </label>
    </FilterBar>
</template>
